const blockedDomainZones = ["ru"];

// A monitored path has to point at a file, not a folder, so the last segment must carry a
// non-empty extension. A leading dot is fine — hidden files such as `.zshrc` are files — and a
// trailing separator leaves the segment empty, which fails here too.
const isFileName = (fileName: string) => {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex > -1 && dotIndex < fileName.length - 1;
};

const fileExtension = (fileName: string) =>
  fileName.slice(fileName.lastIndexOf(".") + 1).toLowerCase();

// macOS bundles carry an extension but are directories on disk, so they are folders as far as
// file-integrity monitoring is concerned.
const macBundleExtensions = [
  "app",
  "appex",
  "bundle",
  "framework",
  "kext",
  "lproj",
  "plugin",
  "prefpane",
  "xpc",
];

/**
 * @deprecated Use `Patterns` from `@coronet/cui` instead. This local copy is a
 * fork of the shared library's patterns and is retained only for call sites that
 * have not yet migrated — it will be removed once they do. Do not add new usages;
 * import `{ Patterns }` from `@coronet/cui`.
 */
const Patterns = {
  ONLY_DIGITS: /^[0-9]\d*$/,
  DIGITS_RANGE: (min: number, max: number) => new RegExp(`^[0-9]{${min},${max}}$`),
  EMAIL:
    /^[a-zA-Z0-9'._%+-]+@(?!(?:\d{1,3}\.){3}\d{1,3}\.)((?!-)[a-zA-Z0-9-]{1,63}(?<!-)\.)+[a-zA-Z]{2,24}$/,
  IP: /^(?:(?:25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\.(?:25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\.(?:25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\.(?:25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(?:\/(3[0-2]|[12]?\d))?|(?:(?:[a-fA-F0-9]{1,4}:){7}[a-fA-F0-9]{1,4}|(?:[a-fA-F0-9]{1,4}:){1,7}:|:(?::[a-fA-F0-9]{1,4}){1,7}|(?:[a-fA-F0-9]{1,4}:){1,6}:[a-fA-F0-9]{1,4}|(?:[a-fA-F0-9]{1,4}:){1,5}(?::[a-fA-F0-9]{1,4}){1,2}|(?:[a-fA-F0-9]{1,4}:){1,4}(?::[a-fA-F0-9]{1,4}){1,3}|(?:[a-fA-F0-9]{1,4}:){1,3}(?::[a-fA-F0-9]{1,4}){1,4}|(?:[a-fA-F0-9]{1,4}:){1,2}(?::[a-fA-F0-9]{1,4}){1,5}|[a-fA-F0-9]{1,4}:(?::[a-fA-F0-9]{1,4}){1,6}|:(?::[a-fA-F0-9]{1,4}){1,7}|:)(?:\/(12[0-8]|1[01]\d|\d{1,2}))?)$/,
  DOMAIN: /^([a-zA-Z0-9]+(-[a-zA-Z0-9]+)*\.)+[a-z]{2,}$/,
  DOMAIN_WITH_UNICODE_SUPPORT:
    /^(?=.{1,253}$)(?:[\p{L}\p{N}](?:[\p{L}\p{N}\p{Pd}]{0,61}[\p{L}\p{N}])?\.)+(?:[\p{L}](?:[\p{L}\p{N}\p{Pd}]{0,61}[\p{L}\p{N}])?)\.?$/u,
  SUBDOMAIN: /^(?!-)[a-zA-Z0-9-]+([a-zA-Z0-9-]+)*[a-zA-Z0-9]$/,
  NO_SPECIAL_CHARACTERS: /^[a-zA-Z0-9]+$/,
  WILDCARD_DOMAIN: /^(\*\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/,
  NOT_ALLOWED_DOMAIN_ZONE: new RegExp(`.*[\`^.\`](${blockedDomainZones.join("|")})$`),
  HEX_COLOR: new RegExp("^#[0-9A-F]{6}$"),
  URL: /^https?:\/\/(?:www\.)?[-a-zA-Z\d@:%._+~#=]{1,256}\.[a-zA-Z\d()]{1,6}\b[-a-zA-Z\d()@:%_+.~#?&/=]*$/,
  SSID: /^[^!#;+\]/"\t ][^+\]/"\t]{0,30}[^ +\]/"\t]$|^[^ !#;+\]/"\t]$[ \t]+$/,
  IP_WITH_OPTIONAL_CIDR:
    /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(?:$|\/([0-9]|[12][0-9]|3[02]))$/,
  IP_WITH_REQUIRED_CIDR:
    /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\/(0?[89]|1[0-9]|2[0-9]|3[0-2])$/,
  SHA_256: /\b[A-Fa-f0-9]{64}\b/,
  CD_HASH: /\b[A-Fa-f0-9]{40}\b/,
  IP_WITHOUT_CIDR: /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)(\.(?!$)|$)){4}$/,
  IP_V4:
    /^((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]?|0)\.){3}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]?|0)$/,
  PORT: /^([1-9][0-9]{0,3}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5])$/,
  PUNYCODE_DOMAIN:
    /^((([a-zA-Z0-9]+(-[a-zA-Z0-9]+)*\.)+[a-z]{2,})|xn--[a-zA-Z0-9]+(\.[a-zA-Z0-9]+)*)$/,
  SEARCH_PREFIX: /^(.*):/,
  TIME: /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
  YEAR: /^\d{4}$/,
  FILE_PATH: (path: string) => {
    const invalidCharacters = /.*['?|].*/;
    if (path.includes(":")) {
      if (path.includes("/")) {
        return false;
      }
      if (!path.includes("\\")) {
        return false;
      }
    } else if (path.startsWith("__")) {
      // if path beginning with env variable like __USERPROFILE__\Documents\any.file
      if (path.includes("\\") && path.includes("/")) {
        return false;
      }
    } else {
      if (path.includes("\\")) {
        return false;
      }
      if (!path.includes("/")) {
        return false;
      }
    }

    return (
      !invalidCharacters.test(path) &&
      !path.includes("\\\\") &&
      !path.includes("//") &&
      path.length >= 2
    );
  },
  // Absolute paths to a file, e.g. C:\ProgramData\Coro\config.json — folders and env variables
  // are not supported.
  WINDOWS_FILE_PATH: (path: string) => {
    const invalidCharacters = /.*['?|].*/;
    const trimmedPath = path.trim();
    // The drive letter is ASCII A-Z: those are the only 26 letters Windows assigns to a volume,
    // so a homoglyph such as Cyrillic "С" names nothing the agent could resolve. Everything after
    // it is unconstrained by script — folder and file names are Unicode on NTFS.
    if (!/^[a-zA-Z]:\\.+/.test(trimmedPath)) {
      return false;
    }
    if (trimmedPath.includes("/")) {
      return false;
    }
    if (!isFileName(trimmedPath.slice(trimmedPath.lastIndexOf("\\") + 1))) {
      return false;
    }

    return (
      !invalidCharacters.test(trimmedPath) &&
      !trimmedPath.includes("\\\\") &&
      !trimmedPath.includes("//") &&
      trimmedPath.length >= 2
    );
  },
  // Absolute paths to a file, e.g. /Library/Preferences/com.coro.agent.plist — folders, bundles
  // and env variables are not supported.
  MAC_FILE_PATH: (path: string) => {
    const invalidCharacters = /.*['?|].*/;
    const trimmedPath = path.trim();
    if (!/^\/.+/.test(trimmedPath)) {
      return false;
    }
    if (trimmedPath.includes("\\")) {
      return false;
    }

    const fileName = trimmedPath.slice(trimmedPath.lastIndexOf("/") + 1);
    if (!isFileName(fileName) || macBundleExtensions.includes(fileExtension(fileName))) {
      return false;
    }

    return (
      !invalidCharacters.test(trimmedPath) &&
      !trimmedPath.includes("\\\\") &&
      !trimmedPath.includes("//") &&
      trimmedPath.length >= 2
    );
  },
  NETWORK_KEY: /^[a-zA-Z0-9!@#$%^&*()]*$/,
  LETTERS_NUMBERS_SPACE: /^[a-zA-Z0-9 ]*$/,
  EMAIL_PROXY_HEADER: /^[a-zA-Z0-9_-]*$/,
};

export default Patterns;
